import { Stack, Switch, Typography, useTheme } from "@mui/material";
import { useAtomValue } from "jotai";
import { Globe, Moon, Shield, Trash2 } from "lucide-react";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import { getLanguageLabel } from "web_myplann/Infrastructure/Translations";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { LanguageDrawer } from "web_myplann/Profile/Components/LanguageDrawer";
import { ListRow } from "web_myplann/Shared/Components/ListRow";
import { LogOutRow } from "web_myplann/Shared/Components/LogOutRow";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import { MyPlannNavigationHeader } from "web_myplann/Shared/Header/MyPlannNavigationHeader";
import { useDarkModeToggle } from "web_myplann/Shared/Hooks/useDarkModeToggle";
import { useLanguagePicker } from "web_myplann/Shared/Hooks/useLanguagePicker";
import { useLogOut } from "web_myplann/Shared/Hooks/useLogOut";
import { appInfoState } from "web_myplann/Shared/State/appInfoAtom";

export const SettingsV2Page: FC = () => {
	const theme = useTheme();
	const { t } = useResource();
	const navigate = useNavigate();
	const appInfoAtom = useAtomValue(appInfoState);
	const { removeAccount } = useLogOut();
	const { isDarkMode, toggle: toggleDarkMode } = useDarkModeToggle();
	const {
		isOpen: isLanguageOpen,
		open: openLanguage,
		close: closeLanguage,
		select: selectLanguage,
	} = useLanguagePicker();

	return (
		<>
			<MyPlannLayout
				disableContentPadding
				bgColor={theme.palette.surface.muted}
				topBar={
					<MyPlannNavigationHeader
						title={t(Resources.Settings.Items.Settings)}
						bgColor={theme.palette.background.paper}
						defaultRoute={Routing.Profile.More.path()}
					/>
				}
			>
				<Stack minHeight="100%">
					<Typography variant="sectionLabel">
						{t(Resources.Settings.Sections.Settings)}
					</Typography>
					<Stack bgcolor={theme.palette.background.paper}>
						<ListRow
							showChevron
							title={t(Resources.Settings.DarkMode)}
							icon={
								<Moon
									size={theme.iconSize.md}
									color={theme.palette.grey[500]}
								/>
							}
							iconBg={theme.palette.grey[100]}
							rightSlot={
								<Switch
									redesigned
									checked={isDarkMode}
									onChange={toggleDarkMode}
								/>
							}
						/>
						<ListRow
							showChevron
							title={t(Resources.Settings.Language)}
							icon={
								<Globe
									size={theme.iconSize.md}
									color={theme.palette.grey[500]}
								/>
							}
							iconBg={theme.palette.grey[100]}
							rightText={t(getLanguageLabel())}
							onClick={openLanguage}
						/>
						<ListRow
							showChevron
							title={t(Resources.Settings.Items.Security)}
							icon={
								<Shield
									size={theme.iconSize.md}
									color={theme.palette.green[600]}
								/>
							}
							iconBg={theme.palette.green[100]}
							isLast
							onClick={() => navigate(Routing.Profile.Security.path())}
						/>
					</Stack>

					<Typography variant="sectionLabel">
						{t(Resources.Settings.Sections.Account)}
					</Typography>
					<Stack bgcolor={theme.palette.background.paper}>
						<LogOutRow />
						<ListRow
							title={t(Resources.Settings.RemoveAccount)}
							icon={
								<Trash2
									size={theme.iconSize.md}
									color={theme.palette.grey[500]}
								/>
							}
							iconBg={theme.palette.grey[100]}
							isLast
							onClick={removeAccount}
						/>
					</Stack>

					<Stack alignItems="center" py={3}>
						<Typography variant="hint">
							{`${t(Resources.Settings.AppVersion)} ${appInfoAtom.appVersion}`}
						</Typography>
					</Stack>
				</Stack>
			</MyPlannLayout>

			<LanguageDrawer
				open={isLanguageOpen}
				onClose={closeLanguage}
				onSelect={selectLanguage}
			/>
		</>
	);
};
